import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { SEO } from './SEO';
import { BookOpen, Network, CreditCard, Server, Shield, ShieldCheck, Wifi, MapPin, Terminal, Globe2, Clock, ArrowRight } from 'lucide-react';
import { WIKI_ARTICLES, WIKI_CATEGORIES, articlesByCategory } from '../data/wiki';

const ICONS = { Network, CreditCard, Server, Shield, ShieldCheck, BookOpen, Wifi, MapPin, Terminal, Globe2 };

export const WikiIndexPage = () => {
  const { language } = useLanguage();
  const ru = language === 'ru';
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const localized = (path) => (ru ? '/ru' + path : path);
  const grouped = articlesByCategory();
  // Render categories in this order, skipping empty buckets:
  const order = ['network', 'billing', 'vps', 'policy'];
  const visibleCats = order.filter((c) => grouped[c]?.length);

  return (
    <div className="min-h-screen bg-white">
      <SEO page="wiki" />
      <Header />

      {/* Hero */}
      <section className="relative py-16 sm:py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <div className="absolute top-10 right-10 w-80 h-80 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 rounded-xl mb-5">
            <BookOpen className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 tracking-tight">
            {ru ? 'База знаний' : 'Knowledge base'}
          </h1>
          <p className="text-lg text-blue-100/80 max-w-2xl mx-auto">
            {ru
              ? 'Прозрачные технические объяснения того, как устроены наши услуги. Пишем здесь то, что обычно остаётся за кадром у хостеров.'
              : 'Plain-English explanations of how our services actually work. The kind of details most providers leave unsaid.'}
          </p>
        </div>
      </section>

      {/* Articles grouped by category */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
          {visibleCats.map((cat) => (
            <div key={cat}>
              <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500 mb-5">
                {WIKI_CATEGORIES[cat][ru ? 'ru' : 'en']}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {grouped[cat].map((article) => {
                  const Icon = ICONS[article.icon] || BookOpen;
                  const c = ru ? article.ru : article.en;
                  return (
                    <Link
                      key={article.slug}
                      to={localized(`/wiki/${article.slug}`)}
                      className="group block rounded-2xl border border-slate-200 bg-white p-6 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-700/10 transition-all"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-blue-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-700 transition-colors">
                            {c.title}
                          </h3>
                          <p className="text-sm text-slate-600 leading-relaxed mb-4">
                            {c.summary}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {article.readingMin} {ru ? 'мин' : 'min'}
                            </span>
                            <span className="opacity-50">·</span>
                            <span>
                              {ru ? 'обновлено ' : 'updated '}{article.updatedAt}
                            </span>
                            <span className="ml-auto inline-flex items-center gap-1 text-blue-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                              {ru ? 'Читать' : 'Read'} <ArrowRight className="w-3.5 h-3.5" />
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}

          {WIKI_ARTICLES.length === 0 && (
            <p className="text-center text-slate-500 py-20">
              {ru ? 'Пока статей нет. Скоро будут.' : 'No articles yet. Soon.'}
            </p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};
